import React, { useMemo } from 'react';
import MedicineStripe from './MedicineStripe';
import { BENEFIT_TYPE, Medicine } from '../../interfaces/medicine';
import { formatToCurrency } from '../../helpers/number';
import { capitalize, capitalizeFirstLetter } from '../../helpers/string';
import { get, isEmpty } from 'lodash';
import MedicineBenefitsChip from './MedicineBenefitsChip';
import { useIsMobile } from '../../helpers/useIsMobile';
import { Text } from '../UI-CHAKRA/Text';
import { Tooltip } from '../UI-CHAKRA/Tooltip';
import { Tag, TagLabel } from '../UI-CHAKRA/Tag';
import { Grid as ChakraGrid } from '../UI-CHAKRA/Grid';
import { Flex } from '../UI-CHAKRA/Flex';
import { GridItem } from '../UI-CHAKRA/GridItem';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { ReactComponent as AlertIcon } from '../../assets/images/icons/mevo/alert.svg';
import { Box } from '../UI-CHAKRA/Box';

interface MedicineProps {
  medicine: Medicine;
}

function MedicineSearchItem(props: MedicineProps) {
  const isMobile = useIsMobile('md');

  const { prescriberConfiguration } = usePrescriptionStore();

  const disablePriceRange = get(prescriberConfiguration, 'Features.disableMedicinePriceRange', false);
  const enablePspBenefit = get(prescriberConfiguration, 'Features.enablePspBenefit', false);

  const medicine = props.medicine;
  const activePrinciples: string = medicine.PrincipiosAtivos.map((principioAtivo) =>
    capitalize(principioAtivo.Nome)
  ).join(', ');

  /**
   * Regras de prioridade das tags:
   * Com filtro ativo:
   * 1. descontinuado
   * 2. beneficio
   *
   * Sem filtro ativo:
   * 1. descontinuado
   * 2. farmacia popular
   * 3. beneficio
   */
  const MedicineLab = useMemo(() => {
    if (medicine.Laboratorio || medicine.TipoMedicamento) {
      const isGeneric = medicine.TipoMedicamento === 'GENERICO';

      return (
        <GridItem colSpan={6} textAlign={'center'} p={'0 8px'} key={2}>
          {!isGeneric && (
            <Text color={'#868E96'} fontSize={'12px'} fontWeight={600} lineHeight={'16px'}>
              {capitalizeFirstLetter(medicine.Laboratorio || '')}
            </Text>
          )}

          {!medicine.ProdutoCorrelato && (
            <Text color={'#868E96'} fontSize={'12px'} fontWeight={400} lineHeight={'16px'}>
              {capitalizeFirstLetter(medicine.TipoMedicamentoComAcentuacao)}
            </Text>
          )}
        </GridItem>
      );
    }
  }, [
    medicine.Laboratorio,
    medicine.TipoMedicamento,
    medicine.TipoMedicamentoComAcentuacao,
    medicine.ProdutoCorrelato,
  ]);

  const medicineNameColumns = () => {
    const size = 24 - 2 - 6 - 4;

    if (!isMobile) {
      return size;
    }
    return 20;
  };

  const showLowestPrice = useMemo(() => {
    return !!medicine.MenorPreco && medicine.MenorPreco !== medicine.MaiorPreco;
  }, [medicine.MaiorPreco, medicine.MenorPreco]);

  const showBiggestPrice = useMemo(() => {
    return !!medicine.MaiorPreco && medicine.MenorPreco !== medicine.MaiorPreco;
  }, [medicine.MaiorPreco, medicine.MenorPreco]);

  const showOnlyprice = useMemo(() => {
    return !!medicine.MaiorPreco && !!medicine.MenorPreco && medicine.MenorPreco === medicine.MaiorPreco;
  }, [medicine.MaiorPreco, medicine.MenorPreco]);

  const Benefits = useMemo(() => {
    if (isEmpty(medicine.Beneficios)) {
      return null;
    }

    const benefitsToShow = medicine.Beneficios.filter((benefit) => {
      const isPsp =
        benefit.Tipo === BENEFIT_TYPE.INDUSTRIA && benefit.Descricao?.startsWith?.('PSP');
      return !isPsp || enablePspBenefit;
    });

    if (isEmpty(benefitsToShow)) {
      return null;
    }

    return (
      <Flex
        alignItems={{ base: 'flex-start', md: 'center' }}
        mt={{ base: '5px', md: '10px' }}
        gap={'10px'}
        flexDir={{ base: 'column', md: 'row' }}
      >
        <Text color={'#868E96'} fontSize={{ base: 12, md: '14px' }} fontWeight={400} lineHeight={'16px'}>
          Disponibilidade:
        </Text>
        <Flex gap={'10px'}>
          {benefitsToShow.map((benefit) => (
            <Tooltip
              maxW={'300px'}
              padding={'15px'}
              backgroundColor={'#000'}
              borderRadius={'0px 8px 8px 8px'}
              fontSize={'12px'}
              label={benefit.Descricao}
              placement={'bottom-start'}
              key={benefit.Tipo}
            >
              <div>
                <MedicineBenefitsChip benefit={benefit} />
              </div>
            </Tooltip>
          ))}
        </Flex>
      </Flex>
    );
  }, [medicine.Beneficios, enablePspBenefit]);

  const PriceLabel = useMemo(() => {
    if (isMobile) return;

    if (medicine.EmFalta) {
      return (
        <GridItem colSpan={4} flex={'0 0 137px'} marginLeft={'auto'}>
          <Tag
            size={'sm'}
            variant={'outlined'}
            borderRadius={'full'}
            backgroundColor={'alert.light'}
            p={'5px 10px'}
            display={'flex'}
            alignItems={'center'}
            gap={'5px'}
          >
            <AlertIcon width={'14px'} />
            <TagLabel fontWeight={500} color={'#3D0E6B'} fontSize={'14px'}>
              Produto em falta
            </TagLabel>
          </Tag>
        </GridItem>
      );
    }

    if (medicine.ProdutoCorrelato || !medicine.AtivoAnvisa || disablePriceRange) return null;

    return (
      <GridItem colSpan={4} style={{ marginLeft: 'auto' }}>
        <Flex alignItems={'flex-end'} justifyContent={'center'} direction={'column'}>
          {showOnlyprice && (
            <Text color={'#868E96'} fontSize={'14px'} fontWeight={400} lineHeight={'16px'}>
              {medicine.MenorPreco ? formatToCurrency(medicine.MenorPreco) : ''}
            </Text>
          )}

          {showLowestPrice && (
            <Text color={'#868E96'} fontSize={'14px'} fontWeight={400} lineHeight={'16px'}>
              de {medicine.MenorPreco ? formatToCurrency(medicine.MenorPreco) : ''}
            </Text>
          )}

          {showBiggestPrice && (
            <Text color={'#868E96'} fontSize={'14px'} fontWeight={400} lineHeight={'16px'}>
              até {medicine.MaiorPreco ? formatToCurrency(medicine.MaiorPreco) : ''}
            </Text>
          )}
        </Flex>
      </GridItem>
    );
  }, [
    disablePriceRange,
    isMobile,
    medicine.AtivoAnvisa,
    medicine.EmFalta,
    medicine.MaiorPreco,
    medicine.MenorPreco,
    medicine.ProdutoCorrelato,
    showBiggestPrice,
    showLowestPrice,
    showOnlyprice,
  ]);

  return (
    <ChakraGrid
      templateColumns={'repeat(24, 1fr)'}
      width={'100%'}
      whiteSpace={'initial'}
      wordBreak={'break-word'}
      alignItems={'center'}
      h={'full'}
      data-cy={`${medicine.Nome.toUpperCase()}`}
    >
      <GridItem
        colSpan={{ base: 4, md: 2 }}
        justifyContent={'center'}
        h={'100%'}
        alignItems={{ base: 'flex-start', md: 'center' }}
        display={'flex'}
      >
        <MedicineStripe medicine={medicine} />
      </GridItem>
      <GridItem colSpan={medicineNameColumns()}>
        <Flex justifyContent={'center'} direction={'column'}>
          {medicine.isPremiumPlacement && (
            <Box bg={'#fff'} borderRadius={'4px'} padding={'5px 8px'} w={'fit-content'} mb={'4px'}>
              <Text fontSize={'12px'} fontWeight={400} color={'typography.darkest'}>
                Destaque
              </Text>
            </Box>
          )}

          <Text color={'typography.darkest'} fontSize={{ base: 12, md: '14px' }} lineHeight={'16px'}>
            {medicine.Label}
          </Text>
          <Text color={'#868E96'} fontSize={{ base: 12, md: '14px' }} fontWeight={400} lineHeight={'16px'} mt={'5px'}>
            {activePrinciples}
          </Text>
        </Flex>
        {Benefits}
      </GridItem>
      {!isMobile && MedicineLab}

      {PriceLabel}

      {!isMobile && !medicine.AtivoAnvisa && (
        <GridItem colSpan={4} flex={'0 0 137px'} marginLeft={'auto'}>
          <Tag
            size={'sm'}
            variant={'outlined'}
            borderRadius={'full'}
            backgroundColor={'white'}
            borderWidth={'1px'}
            borderColor={'#868E96'}
          >
            <TagLabel fontWeight={500} color={'#868E96'}>
              Descontinuado
            </TagLabel>
          </Tag>
        </GridItem>
      )}
    </ChakraGrid>
  );
}

export default React.memo(MedicineSearchItem);
